import React, { useEffect } from "react";
import { useTutorSessions } from "src/hooks/useTutorSessions";
import { useTutorSessionsStore } from "src/store/tutorSessionsStore";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/Table";
import { Input } from "src/components/ui/Input";
import { Button } from "src/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "src/components/ui/DropdownMenu";
import { Badge } from "src/components/ui/Badge";
import {
  Search,
  Filter,
  Columns,
  Calendar,
  MoreHorizontal,
  Send,
  RefreshCw,
  Video,
  Clock,
  Calendar as CalendarIcon,
  Star,
  User,
  AlertCircle,
  Grid,
  List,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/Dialog";
import LoadingSpinner from "src/components/LoadingSpinner";
import { Link } from "react-router-dom";
import TutorSessionCard, { getStudentDisplayName, getStudentPhoto, getStudentInitials, formatSessionTime, getTopicName, getSubtopicName, generateSessionId, getSubjectName } from "src/components/tutor/TutorSessionCard";
import AuthenticatedImage from "src/components/ui/AuthenticatedImage";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import NotificationDropdown from "src/components/notifications/NotificationDropdown";
import { getSessionStatus } from "src/utils/sessionUtils";

const TutorUpcomingSessions = () => {
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  useEffect(() => {
    setTitle("Upcoming Sessions");
    setDescription("Manage your scheduled teaching sessions and stay organized");
    setActions(<NotificationDropdown />);
    return () => reset();
  }, [setTitle, setDescription, setActions, reset]);

  // Use real sessions hook for data fetching
  const {
    isLoading: isLoadingSessions,
    error: sessionsError,
    refreshSessions
  } = useTutorSessions();

  // Get UI state and actions from Zustand store
  const {
    searchTerm,
    setSearchTerm,
    isCardView,
    setIsCardView,
    selectedSession,
    setSelectedSession,
    showReminderDialog,
    setShowReminderDialog,
    visibleColumns,
    setVisibleColumns,
    filters,
    toggleFilter,
    clearAllFilters,
    getFilteredSessions,
    getFilterOptions,
    hasActiveFilters,
  } = useTutorSessionsStore();

  // Get computed data from store
  const filteredSessions = getFilteredSessions();
  const { statuses, sessionTypes, subjects } = getFilterOptions();
  const activeFilters = hasActiveFilters();

  const handleJoinSession = (session: any) => {
    // Navigate to join session page
    window.open(`/join-session/${session.id}`, '_blank');
  };

  const handleSendReminder = (session: any) => {
    setSelectedSession(session);
    setShowReminderDialog(true);
  };

  const confirmSendReminder = () => {
    if (selectedSession) {
      console.log(`Sending reminder for session ${selectedSession.id}`);
      // TODO: Implement actual reminder sending logic
      setShowReminderDialog(false);
      setSelectedSession(null);
    }
  };

  const handleActionsClick = (session: any, action: string) => {
    if (action === "Join") {
      handleJoinSession(session);
    } else if (action === "Send Reminder") {
      handleSendReminder(session);
    }
  };

  const getActionButtons = (session: any) => {
    const sessionStatus = getSessionStatus(session);
    
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {sessionStatus.canJoin && (
            <DropdownMenuItem 
              onClick={() => handleActionsClick(session, "Join")}
              className={sessionStatus.status === 'live' ? 'bg-red-50 text-red-700 font-medium' : ''}
            >
              <Video className="mr-2 h-4 w-4" />
              {sessionStatus.status === 'live' ? 'Join Now' : 'Join'}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => handleActionsClick(session, "Send Reminder")}>
            <Send className="mr-2 h-4 w-4" />
            Send Reminder
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <>
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
        <div className="relative flex-grow max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by student, subject, or topic"
            className="pl-10 pr-4 py-2 w-full border-gray-200 rounded-md focus:border-rfpurple-500 focus:ring focus:ring-rfpurple-200 focus:ring-opacity-50 transition-colors"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Filter size={16} />
                Filter
                {activeFilters && (
                  <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-xs">
                    {filters.status.length + filters.sessionType.length + filters.subject.length}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <div className="p-2 text-sm font-medium text-gray-700 border-b">
                Filter Options
              </div>

              {/* Status Filter */}
              <div className="p-2">
                <div className="text-xs font-medium text-gray-600 mb-2">Status</div>
                {statuses.map(status => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={filters.status.includes(status)}
                    onCheckedChange={() => toggleFilter('status', status)}
                    className="capitalize"
                  >
                    {status}
                  </DropdownMenuCheckboxItem>
                ))}
              </div>

              {/* Session Type Filter */}
              <div className="p-2 border-t">
                <div className="text-xs font-medium text-gray-600 mb-2">Session Type</div>
                {sessionTypes.map(type => (
                  <DropdownMenuCheckboxItem
                    key={type}
                    checked={filters.sessionType.includes(type)}
                    onCheckedChange={() => toggleFilter('sessionType', type)}
                    className="capitalize"
                  >
                    {type}
                  </DropdownMenuCheckboxItem>
                ))}
              </div>

              {/* Subject Filter */}
              <div className="p-2 border-t">
                <div className="text-xs font-medium text-gray-600 mb-2">Subject</div>
                {subjects.map(subject => (
                  <DropdownMenuCheckboxItem
                    key={subject}
                    checked={filters.subject.includes(subject)}
                    onCheckedChange={() => toggleFilter('subject', subject)}
                  >
                    {subject}
                  </DropdownMenuCheckboxItem>
                ))}
              </div>

              {activeFilters && (
                <div className="p-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearAllFilters}
                    className="w-full"
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Columns size={16} />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="p-2 text-sm font-medium text-gray-700 border-b">
                Toggle Columns
              </div>
              {Object.entries(visibleColumns).map(([key, value]) => (
                <DropdownMenuCheckboxItem
                  key={key}
                  checked={value}
                  onCheckedChange={(checked) =>
                    setVisibleColumns({ ...visibleColumns, [key]: checked })
                  }
                  className="capitalize"
                >
                  {key === "sessionId" ? "Session ID" :
                   key === "dateTime" ? "Date & Time" :
                   key === "sessionType" ? "Session Type" : key}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsCardView(!isCardView)}
            className="flex items-center gap-2"
          >
            {isCardView ? <List size={16} /> : <Grid size={16} />}
            {isCardView ? "Table View" : "Card View"}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={refreshSessions}
            className="flex items-center gap-2"
            disabled={isLoadingSessions}
          >
            <RefreshCw size={16} className={isLoadingSessions ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Content Area */}
      {isLoadingSessions ? (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner />
          <span className="ml-2">Loading sessions...</span>
        </div>
      ) : sessionsError ? (
        <div className="text-center py-6">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{sessionsError}</p>
          <Button onClick={refreshSessions} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      ) : filteredSessions.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500 mb-4">
            {filteredSessions.length === 0 && !activeFilters
              ? "You don't have any upcoming sessions."
              : "No sessions match your search criteria."}
          </p>
          {filteredSessions.length === 0 && !activeFilters && (
            <p className="text-sm text-gray-400">
              Students will book sessions with you based on your availability.
            </p>
          )}
        </div>
      ) : isCardView ? (
        // Card View
        <div className="p-6 space-y-6">
          {filteredSessions.map((session) => (
            <TutorSessionCard
              key={session.id}
              session={session}
              showSessionId={visibleColumns.sessionId}
              onJoin={handleJoinSession}
              onSendReminder={handleSendReminder}
            />
          ))}
        </div>
      ) : (
        // Table View
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {visibleColumns.sessionId && <TableHead>Session ID</TableHead>}
                {visibleColumns.student && <TableHead>Student</TableHead>}
                {visibleColumns.subject && <TableHead>Subject</TableHead>}
                {visibleColumns.topic && <TableHead>Topic</TableHead>}
                {visibleColumns.subtopic && <TableHead>Subtopic</TableHead>}
                {visibleColumns.sessionType && <TableHead>Session Type</TableHead>}
                {visibleColumns.dateTime && <TableHead>Date & Time</TableHead>}
                {visibleColumns.duration && <TableHead>Duration</TableHead>}
                {visibleColumns.status && <TableHead>Status</TableHead>}
                {visibleColumns.actions && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSessions.map((session) => {
                const { date, time } = formatSessionTime(session.scheduled_at);
                const studentDisplayName = getStudentDisplayName(session.student_profile);
                const studentPhoto = getStudentPhoto(session.student_profile);
                const studentInitials = getStudentInitials(session.student_profile);
                const topicName = getTopicName(session.topic);
                const sessionId = generateSessionId(session.id);
                const sessionStatus = getSessionStatus(session);

                // Debug: Log the student profile data for each session
                console.log('Table row - Student profile:', session.student_profile);
                console.log('Table row - Student photo URL:', studentPhoto);

                return (
                  <TableRow key={session.id}>
                    {visibleColumns.sessionId && (
                      <TableCell className="font-medium">{sessionId}</TableCell>
                    )}
                    {visibleColumns.student && (
                      <TableCell>
                        <div className="flex items-center">
                          {studentPhoto ? (
                            <AuthenticatedImage
                              src={studentPhoto}
                              alt={studentDisplayName}
                              className="w-8 h-8 rounded-full object-cover mr-2"
                              fallback={
                                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-xs mr-2">
                                  {studentInitials}
                                </div>
                              }
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-xs mr-2">
                              {studentInitials}
                            </div>
                          )}
                          <div>
                            <div className="font-medium">{studentDisplayName}</div>
                          </div>
                        </div>
                      </TableCell>
                    )}
                    {visibleColumns.subject && (
                      <TableCell>
                        <Badge variant="outline">{getSubjectName(session.topic)}</Badge>
                      </TableCell>
                    )}
                    {visibleColumns.topic && (
                      <TableCell>
                        <div>
                          <div className="font-medium">{topicName}</div>
                          {session.batch?.name && (
                            <p className="text-xs text-gray-500 mt-1">
                              {session.batch.name}
                            </p>
                          )}
                        </div>
                      </TableCell>
                    )}
                    {visibleColumns.subtopic && (
                      <TableCell>
                        {getSubtopicName(session.subtopic) || '—'}
                      </TableCell>
                    )}
                    {visibleColumns.sessionType && (
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {session.session_type}
                        </Badge>
                      </TableCell>
                    )}
                    {visibleColumns.dateTime && (
                      <TableCell>
                        <div>
                          <div className="font-medium">{date}</div>
                          <div className="text-sm text-gray-500">{time}</div>
                        </div>
                      </TableCell>
                    )}
                    {visibleColumns.duration && (
                      <TableCell>{session.duration_min} min</TableCell>
                    )}
                    {visibleColumns.status && (
                      <TableCell>
                        <Badge 
                          variant={sessionStatus.variant}
                          className={`capitalize ${sessionStatus.status === 'live' ? 'animate-pulse' : ''}`}
                        >
                          {sessionStatus.label}
                        </Badge>
                      </TableCell>
                    )}
                    {visibleColumns.actions && (
                      <TableCell>{getActionButtons(session)}</TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>

    {/* Send Reminder Dialog */}
    <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send Session Reminder</DialogTitle>
          <DialogDescription>
            {selectedSession && (
              <>
                Send a reminder to{' '}
                {getStudentDisplayName(selectedSession.student_profile)}{' '}
                about the upcoming session on {formatSessionTime(selectedSession.scheduled_at).date} at{' '}
                {formatSessionTime(selectedSession.scheduled_at).time}?
                <br /><br />
                <strong>Topic:</strong> {getTopicName(selectedSession.topic)}
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowReminderDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmSendReminder}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Send className="h-4 w-4 mr-2" />
            Send Reminder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default TutorUpcomingSessions;